/**
 * Injects Google Analytics 4, GTM, Meta Pixel, Microsoft Clarity, and verification
 * meta tags into <head> based on IDs configured in /admin/analytics.
 *
 * Fetches the (public) config from /api/analytics/config once at mount.
 * Skips gracefully if any ID is blank.
 */
import { useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function injectScript(id, src, inline = "", async = true) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = async;
  if (src) s.src = src;
  if (inline) s.text = inline;
  document.head.appendChild(s);
}

function injectMeta(id, name, content) {
  if (!content || document.getElementById(id)) return;
  const m = document.createElement("meta");
  m.id = id;
  m.name = name;
  m.content = content;
  document.head.appendChild(m);
}

function injectNoscript(id, html) {
  if (document.getElementById(id)) return;
  const n = document.createElement("noscript");
  n.id = id;
  n.innerHTML = html;
  document.body.insertBefore(n, document.body.firstChild);
}

export default function TrackingTags() {
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/analytics/config`).then(({ data }) => {
      if (cancelled) return;
      // GA4
      if (data.ga4_measurement_id) {
        const id = data.ga4_measurement_id.trim();
        injectScript("lx6-ga4-lib", `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`);
        injectScript("lx6-ga4-init", "", `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${id}', { anonymize_ip: true });
        `, false);
      }
      // GTM
      if (data.gtm_container_id) {
        const id = data.gtm_container_id.trim();
        injectScript("lx6-gtm-init", "", `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
          var f=d.getElementsByTagName(s)[0], j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
          j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
          f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');
        `, false);
        injectNoscript("lx6-gtm-noscript",
          `<iframe src="https://www.googletagmanager.com/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`);
      }
      // Meta Pixel
      if (data.meta_pixel_id) {
        const id = data.meta_pixel_id.trim();
        injectScript("lx6-meta-pixel", "", `
          !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
          n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
          document,'script','https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${id}'); fbq('track', 'PageView');
        `, false);
      }
      // Microsoft Clarity
      if (data.clarity_project_id) {
        const id = data.clarity_project_id.trim();
        injectScript("lx6-clarity", "", `
          (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "${id}");
        `, false);
      }
      // GSC + FB Domain verification meta
      injectMeta("lx6-gsc", "google-site-verification", data.gsc_verification);
      injectMeta("lx6-fb-domain", "facebook-domain-verification", data.fb_domain_verification);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return null;
}
